"use client";

import { useEffect, useRef, useState } from "react";
import { Clock, Globe, Shield, Zap } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Metric = {
  icon: "zap" | "shield" | "globe" | "clock";
  value: number;
  suffix: string;
  prefix: string;
  decimals: number;
  display: string;
  label: string;
  detail: string;
};
const metrics: Metric[] = [{
  icon: "clock",
  value: 21,
  suffix: "+",
  prefix: "",
  decimals: 0,
  display: "21+",
  label: "Years of Care",
  detail: "Serving Parkwoods-Donalda families since 2005"
}, {
  icon: "shield",
  value: 8,
  suffix: ":1",
  prefix: "",
  decimals: 0,
  display: "8:1",
  label: "Student Ratio",
  detail: "Personalized attention for every child"
}, {
  icon: "globe",
  value: 100,
  suffix: "%",
  prefix: "",
  decimals: 0,
  display: "100%",
  label: "CWELCC",
  detail: "Proud participant in the national program"
}, {
  icon: "zap",
  value: 2,
  suffix: ".5-6",
  prefix: "",
  decimals: 1,
  display: "2.5-6",
  label: "Age Range",
  detail: "Developmentally appropriate Montessori curriculum"
}];
function iconFor(name: Metric["icon"]) {
  if (name === "zap") return Zap;
  if (name === "shield") return Shield;
  if (name === "globe") return Globe;
  return Clock;
}
function AnimatedCounter({
  metric,
  start,
  index
}: {
  metric: Metric;
  start: boolean;
  index: number;
}) {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    if (!start) return;
    const duration = 1800;
    const startTime = performance.now();
    const from = 0;
    const to = metric.value;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(from + (to - from) * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [start, metric.value]);
  const formatted = metric.decimals > 0 ? value.toFixed(metric.decimals) : Math.round(value).toString();
  return <span data-index={index} className="bg-clip-text text-transparent" style={{
    backgroundImage: "linear-gradient(135deg, hsl(145 30% 12%) 0%, hsl(28 35% 45%) 50%, hsl(45 90% 45%) 100%)"
  }}>
      {metric.prefix}
      {formatted}
      {metric.suffix}
    </span>;
}
export default function Foundation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.25
  });
  return <section data-section-id="194" id="our-foundation-of-trust" className="bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-60" style={{
      background: "radial-gradient(60% 50% at 50% 0%, hsla(145, 30%, 12%, 0.05) 0%, transparent 70%), radial-gradient(40% 40% at 80% 100%, hsla(45, 90%, 45%, 0.05) 0%, transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="mx-auto max-w-3xl text-center mb-14 md:mb-20">
          <div className={`inline-flex items-center gap-2 rounded-md border border-border bg-card/50 px-3 py-1.5 backdrop-blur-sm transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
            <span className="h-1.5 w-1.5 rounded-md bg-primary" />
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Our Commitment
            </span>
          </div>

          <h2 className={`font-default mt-6 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-foreground transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
            Nurturing potential,
            <br />
            <span className="bg-clip-text text-transparent italic font-serif" style={{
            backgroundImage: "linear-gradient(135deg, hsl(145 30% 12%) 0%, hsl(28 35% 45%) 60%, hsl(45 90% 45%) 100%)"
          }}>
              growing together.
            </span>
          </h2>
        </div>

        <div ref={ref} className={`relative rounded-3xl border border-border bg-card/60 backdrop-blur-md p-6 md:p-10 lg:p-14 shadow-lg transition-all duration-700 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
        backgroundImage: "linear-gradient(180deg, hsla(40, 33%, 96%, 0.7) 0%, hsla(40, 33%, 90%, 0.5) 100%)"
      }}>
          <div aria-hidden="true" className="absolute inset-x-10 top-0 h-px" style={{
          background: "linear-gradient(90deg, transparent 0%, hsla(145, 30%, 12%, 0.2) 50%, transparent 100%)"
        }} />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 md:gap-y-0">
            {metrics.map((metric, i) => {
            const Icon = iconFor(metric.icon);
            const isLastCol = i === metrics.length - 1;
            const isRightEdgeMobile = i % 2 === 1;
            return <div data-index={i} key={metric.label} className="group relative flex flex-col items-center text-center px-4 md:px-6">
                  {!isLastCol && <div aria-hidden="true" className={`hidden md:block absolute right-0 top-1/2 -translate-y-1/2 h-3/4 w-px transition-opacity duration-300 ${"group-hover:opacity-100"}`} style={{
                background: "linear-gradient(180deg, transparent 0%, hsla(145, 10%, 10%, 0.1) 50%, transparent 100%)"
              }} />}

                  {!isRightEdgeMobile && <div aria-hidden="true" className="md:hidden absolute right-0 top-1/2 -translate-y-1/2 h-3/4 w-px" style={{
                background: "linear-gradient(180deg, transparent 0%, hsla(145, 10%, 10%, 0.1) 50%, transparent 100%)"
              }} />}

                  <div className="relative mb-5 flex h-12 w-12 items-center justify-center rounded-xl border border-border transition-all duration-300 group-hover:scale-105" style={{
                background: "linear-gradient(135deg, hsla(45, 90%, 45%, 0.15) 0%, hsla(28, 35%, 45%, 0.1) 100%)"
              }}>
                    <div aria-hidden="true" className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{
                  boxShadow: "0 0 24px 0 hsla(45, 90%, 45%, 0.3), inset 0 0 12px 0 hsla(45, 90%, 45%, 0.2)"
                }} />
                    <Icon className="relative h-5 w-5" style={{
                  color: "hsl(145 30% 12%)"
                }} strokeWidth={2} />
                  </div>

                  <div className="font-default text-5xl md:text-6xl lg:text-[4rem] font-bold leading-none tracking-tight">
                    <AnimatedCounter metric={metric} start={isVisible} index={i} />
                  </div>

                  <div className="mt-4 font-mono text-xs uppercase tracking-widest text-foreground/80">
                    {metric.label}
                  </div>

                  <p className="mt-2 text-sm text-muted-foreground max-w-[200px]">
                    {metric.detail}
                  </p>
                </div>;
          })}
          </div>

          <div aria-hidden="true" className="absolute inset-x-10 bottom-0 h-px" style={{
          background: "linear-gradient(90deg, transparent 0%, hsla(28, 35%, 45%, 0.2) 50%, transparent 100%)"
        }} />
        </div>
      </div>
    </section>;
}